import getDomain from '../helpers/getDomain';
import axios from 'axios';
import React, { useState, createContext, useContext, useCallback } from 'react';
import { AlertContext } from './AlertContext';
import {
  getBasketToken,
  removeBasketToken,
  setBasketToken,
  isBasketToken,
} from '../helpers/basketToken';
import { removeOrderToken } from '../helpers/orderToken';
import TagManager from 'react-gtm-module';
import getCardinalPointFromStoneId from '../helpers/getCardinalPointFromStoneId';
import getTierFromPrice from '../helpers/getTierFromPrice';

export const BasketContext = createContext();

export const BasketProvider = (props) => {
  const [basket, updateBasket] = useState({});
  const [, showAlert] = useContext(AlertContext);

  const getHeader = useCallback(() => {
    return {
      Authorization: `Bearer ${getBasketToken()}`,
    };
  }, []);

  const errorHandler = useCallback(
    (error) => {
      if (error.response) {
        console.error(error.response.data);

        showAlert({
          status: 'warning',
          text: error.response.data.message,
        });

        if (
          error.response.data.type === 'CartExpiredException' ||
          error.response.data.type === 'LineNotFoundException'
        ) {
          removeOrderToken();
          removeBasketToken();
          updateBasket({});
        }
      } else if (error.request) {
        console.error(error.request);

        removeOrderToken();
        removeBasketToken();
        updateBasket({});
      } else {
        console.error('Error', error.message);

        removeOrderToken();
        removeBasketToken();
        updateBasket({});
      }
    },
    [showAlert]
  );

  const createNewBasket = useCallback(
    (stoneElement, addItemToBasket) => {
      axios
        .post(getDomain() + '/api/cart/begin')
        .then((response) => {
          setBasketToken(response.data.token);

          if (stoneElement) {
            addItemToBasket(stoneElement);
          }
        })
        .catch((error) => {
          errorHandler(error);
        });
    },
    [errorHandler]
  );

  const addItemToBasket = useCallback(
    async (stoneElement) => {
      if (!isBasketToken()) {
        createNewBasket(stoneElement, addItemToBasket);
      } else {
        axios
          .post(
            getDomain() + '/api/cart/add',
            {
              StoneId: stoneElement.id,
            },
            {
              headers: getHeader(),
            }
          )
          .then((response) => {
            updateBasket({
              ...response.data,
            });

            stoneElement.classList.add('added-to-basket');

            const line = response.data.lines[response.data.lines.length - 1];

            TagManager.dataLayer({
              dataLayer: {
                event: 'addToCart',
                ecommerce: {
                  currencyCode: 'GBP',
                  add: {
                    products: [
                      {
                        name: line.stoneId,
                        id: line.stoneId,
                        price: line.price,
                        brand: 'White Tower',
                        category: `The ${getCardinalPointFromStoneId(
                          line.stoneId
                        )} Side`,
                        variant: getTierFromPrice(line.price),
                        quantity: 1,
                      },
                    ],
                  },
                },
              },
            });
          })
          .catch((error) => {
            errorHandler(error);
          });
      }
    },
    [createNewBasket, errorHandler, getHeader]
  );

  const removeItemFromBasket = useCallback(
    async (lineId, stoneId) => {
      axios
        .delete(getDomain() + '/api/cart/line/' + lineId, {
          headers: getHeader(),
        })
        .then((response) => {
          updateBasket({
            ...response.data,
          });

          const stone = document.querySelector('#' + stoneId);

          if (stone) {
            stone.classList.remove('added-to-basket');
          }
        })
        .catch((error) => {
          errorHandler(error);
        });
    },
    [errorHandler, getHeader]
  );

  const getBasket = useCallback(async () => {
    axios
      .get(getDomain() + '/api/cart/', {
        headers: getHeader(),
      })
      .then((response) => {
        updateBasket({
          ...response.data,
        });
      })
      .catch((error) => {
        errorHandler(error);
      });
  }, [errorHandler, getHeader]);

  return (
    <BasketContext.Provider
      value={[
        basket,
        addItemToBasket,
        removeItemFromBasket,
        getBasket,
        updateBasket,
      ]}
    >
      {props.children}
    </BasketContext.Provider>
  );
};
